import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Role } from '@/api/access/accessApi.types';
import { ListPagination } from '@/components/admin/shared/ListPagination';
import { ListSearchInput } from '@/components/admin/shared/ListSearchInput';
import { QueryErrorState } from '@/components/admin/shared/QueryErrorState';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Icon } from '@/components/common/Icon';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { ADMIN_LIST_PAGE_SIZE, SEARCH_DEBOUNCE_MILLISECONDS } from '@/constants/adminPagination';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { useHasPlatformPermission } from '@/hooks/useHasPlatformPermission';
import { useRolesQuery } from '@/hooks/useRolesQuery';
import { RoleFormModal } from './RoleFormModal';

export function RolesSection() {
  const { t } = useTranslation(['admin', 'common']);
  const [searchInput, setSearchInput] = useState('');
  const debouncedSearch = useDebouncedValue(searchInput, SEARCH_DEBOUNCE_MILLISECONDS);
  const [page, setPage] = useState(1);

  useEffect(
    function resetPageOnSearchChange() {
      setPage(1);
    },
    [debouncedSearch]
  );

  const rolesQuery = useRolesQuery(page, debouncedSearch);
  const canCreateRole = useHasPlatformPermission('role.create');

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingRoleId, setEditingRoleId] = useState<string | null>(null);

  function openCreateModal() {
    setEditingRoleId(null);
    setIsModalOpen(true);
  }

  function openRoleModal(role: Role) {
    setEditingRoleId(role.id);
    setIsModalOpen(true);
  }

  function closeModal() {
    setIsModalOpen(false);
    setEditingRoleId(null);
  }

  const rolesResult = rolesQuery.data;

  return (
    <div>
      {canCreateRole === true ? (
        <div className="flex justify-end mb-5">
          <Button onClick={openCreateModal}>
            <Icon name="Plus" />
            {t('admin:roles.createAction')}
          </Button>
        </div>
      ) : null}

      <div className="mb-5">
        <ListSearchInput
          value={searchInput}
          onChange={setSearchInput}
          placeholder={t('admin:roles.searchPlaceholder')}
        />
      </div>

      {rolesQuery.isLoading === true ? (
        <div className="py-16 flex justify-center">
          <LoadingSpinner className="w-7 h-7" />
        </div>
      ) : rolesQuery.isError === true || rolesResult === undefined ? (
        <QueryErrorState
          onRetry={function retryRoles() {
            rolesQuery.refetch();
          }}
        />
      ) : (
        <>
          <RolesTable
            roles={rolesResult.items}
            hasSearch={debouncedSearch.trim().length > 0}
            onSelectRole={openRoleModal}
          />
          <ListPagination
            page={page}
            pageSize={ADMIN_LIST_PAGE_SIZE}
            totalItems={rolesResult.totalItems}
            onPageChange={setPage}
          />
        </>
      )}

      {isModalOpen === true ? <RoleFormModal roleId={editingRoleId} onClose={closeModal} /> : null}
    </div>
  );
}

interface RolesTableProps {
  roles: Role[];
  hasSearch: boolean;
  onSelectRole: (role: Role) => void;
}

function RolesTable({ roles, hasSearch, onSelectRole }: RolesTableProps) {
  const { t } = useTranslation('admin');

  if (roles.length === 0) {
    return (
      <Card padding="lg" shadow={false} className="text-center text-sm text-zoomuted">
        {hasSearch === true ? t('roles.noResults') : t('roles.emptyList')}
      </Card>
    );
  }

  return (
    <Card padding="none" className="overflow-hidden divide-y divide-zooborder">
      {roles.map(function renderRoleRow(role) {
        return (
          <button
            key={role.id}
            type="button"
            onClick={function selectRole() {
              onSelectRole(role);
            }}
            className="w-full flex items-center gap-3 px-4 sm:px-5 py-4 text-left hover:bg-zoovpalelavender transition-colors"
          >
            <div className="w-10 h-10 rounded-xl bg-zoovpalelavender text-zooprimary flex items-center justify-center shrink-0">
              <Icon name="ShieldCheck" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-bold text-zooindigo truncate">{role.name}</div>
              {role.description === null ? null : (
                <div className="text-xs text-zoomuted truncate">{role.description}</div>
              )}
            </div>
            <Badge tone="neutral" size="sm" className="hidden sm:inline-flex shrink-0">
              {t('scope.' + role.scopeCode)}
            </Badge>
            <Badge
              tone={role.isSystem === true ? 'accent' : 'success'}
              size="sm"
              className="shrink-0"
            >
              {role.isSystem === true ? t('roles.badgeSystem') : t('roles.badgeCustom')}
            </Badge>
            <Icon name="ChevronRight" className="text-zoomuted shrink-0" />
          </button>
        );
      })}
    </Card>
  );
}
